import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useLifeEventApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import GenericFormDrawer from '../../components/GenericFormDrawer';

const Container = styled.div``;

export default function LifeEventsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { columns: baseColumns, data, fetching, pagination, updateStatus, create, objectSchema } = useLifeEventApi(queryParams);

  const handleNewButtonClick = useCallback(() => setDrawerOpen(true), []);
  const handleSave = useCallback(async (formData) => {
    const ok = await create(formData);
    if (ok) setDrawerOpen(false);
    return ok;
  }, [create]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const handleAcknowledge = useCallback((row) => updateStatus(row.id, 'acknowledged'), [updateStatus]);
  const handleResolve = useCallback((row) => updateStatus(row.id, 'resolved'), [updateStatus]);
  const handleExpire = useCallback((row) => updateStatus(row.id, 'expired'), [updateStatus]);

  const actionOptions = useMemo(() => {
    if (!canWrite('BN')) return [];
    return (row) => [
      ...(row?.status === 'submitted' ? [{ type: 'acknowledge', label: t('lifeEvent.acknowledge'), onClick: handleAcknowledge }] : []),
      ...(row?.status !== 'resolved' ? [{ type: 'resolve', label: t('lifeEvent.resolve'), onClick: handleResolve }] : []),
      ...(row?.status === 'submitted' ? [{ type: 'expire', label: t('lifeEvent.expire'), onClick: handleExpire }] : []),
    ];
  }, [t, canWrite, handleAcknowledge, handleResolve, handleExpire]);

  const columns = useMemo(() => {
    if (!canWrite('BN')) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const opts = actionOptions(row);
            return opts.length > 0 ? <OptionsMenu actions={opts} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data, canWrite]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('lifeEvent.newTitle', { defaultValue: 'Yeni Hayat Olayı' })}
        schema={objectSchema || []}
        initialValues={null}
        onSave={handleSave}
      />

      {useMemo(() => (
        <DataTable
          title={t('lifeEvent.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('BN')}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite, handleNewButtonClick,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
